/**
 * Page class is intended to capture different properties for HTML pages
 * Supply those properties for services to use them (ie. analytics to mParticle).
 *
 * Any specific formatting of properties or types should be done in concrete classes for the third-part provider, not
 * here.
 */
import { AdService } from '../services/ads'
import { AgeGateService } from '../services/age-gate'
import { AgeGateEvents, PageAgeGateEvents } from '../services/age-gate/event'
import { PageAgeGateService } from '../services/age-gate/page'
import { AnalyticsService, IAnalyticsServiceConfiguration } from '../services/analytics'
import { AnalyticsMetaKeys as key } from '../services/analytics/formatters/meta-keys'
import { IdentityEvent, IIdentityEvent } from '../services/common/events/identity'
import { IProfile, Profile } from '../services/common/profile'
import { ConsentBannerService } from '../services/consent-banner'
import { IdentityService } from '../services/identity'
import { ISubscription } from '../services/identity/adapters/adapter'
import { WidgetService } from '../services/widgets'
import { Utils } from '../utils/common'
import { Subscription } from './analytics/providers/mparticle/events/user/subscription'
import { IConfiguration } from './conf'
import { ICommentAdConfiguration } from './conf/ads'

export class Page {
    public profile: Profile
    public pageStartTime: number
    protected analyticsConf: IAnalyticsServiceConfiguration
    private widgetService = new WidgetService()

    constructor(
        private conf: IConfiguration,
        private adService: AdService,
        private ageGateService: AgeGateService,
        private pageAgeGateService: PageAgeGateService,
        private consentBannerService: ConsentBannerService,
        private analyticsService: AnalyticsService,
        private identityService: IdentityService) {

        // create analytics config
        if (this.conf.analytics && this.analyticsService) {
            this.analyticsConf = {
                adSite: this.conf.ads.site,
                brand: this.conf.analytics.brand,
                debug: this.conf.analytics.debug,
                division: this.conf.analytics.division,
                domain: this.conf.analytics.domain,
                femVersion: this.conf.analytics.femVersion,
                page: this.conf.page,
                platform: this.conf.analytics.platform,
                platformVersion: this.conf.analytics.platformVersion,
                profile: undefined,
                provider: this.conf.analytics.provider,
                tags: this.conf.analytics.tags,
                vendor: this.conf.analytics.vendor
            }
        }

        // init identity service first; dependent services init'd in change handler
        if (this.conf.identity) {
            let commentAdConfiguration: ICommentAdConfiguration

            /* pass in comment ad config if present */

            if(this.conf.ads) {
                commentAdConfiguration = this.conf.ads.commentAdConfiguration
            }

            this.identityService
                .addEventListener(IdentityEvent.EMAIL_VERIFIED, this.onEmailVerified.bind(this))
                .addEventListener(IdentityEvent.IDENTITY_INIT, this.onIdentityInit.bind(this))
                .addEventListener(IdentityEvent.IDENTITY_CHANGE, this.onIdentityChange.bind(this))
                .addEventListener(IdentityEvent.SESSION_INIT, this.onIdentitySession.bind(this))
                .initialize(
                    {
                        ...conf.identity.sso,
                        page: this.conf.page,
                        showPasswordPolicy: this.conf.identity.showPasswordPolicy
                    },
                    conf.identity.subscriber,
                    conf.identity.links,
                    conf.identity.logo,
                    conf.page.isPremium,
                    conf.page.isPaywallWhitelisted,
                    conf.identity.property,
                    commentAdConfiguration
                    )
        } else {
            /**
             * no configuration for identity, but still initialize for sake of profile handling
             * like age gating
             */
            this.identityService
                .addEventListener(IdentityEvent.IDENTITY_INIT, this.onIdentityInit.bind(this))
                .addEventListener(IdentityEvent.IDENTITY_CHANGE, this.onIdentityChange.bind(this))
                .initialize(undefined, undefined, undefined, undefined)
            this.onIdentitySession(Utils.event(IdentityEvent.SESSION_INIT, this.profile))
        }

        this.pageAgeGateService
            .addEventListener(PageAgeGateEvents.VERIFIED, this.onAgeVerified.bind(this))

        this.ageGateService
            .addEventListener(AgeGateEvents.VERIFIED, this.onAgeVerified.bind(this))

        this.consentBannerService.initialize()
    }

    private onAgeVerified() {
        this.identityService.update({ ageOfMajority: true } as IProfile).catch(console.log)
    }

    private onEmailVerified(event: IIdentityEvent) {
        // @todo: @Dev please move this to analytics service, e.g.: `this.analyticsService.emailVerified()`
        // trigger email verified event for mparticle
        const accountConfirmedEvent = new Subscription(
            AnalyticsService.pageLoadEventTypes.accountConfirmed,
            JSON.stringify({tOf: 'true'}),
            AnalyticsService.ACTION_PAGE_LOAD)
        accountConfirmedEvent.setMetaProperties({[key.DOMAIN]: this.conf.analytics.domain})
        accountConfirmedEvent.handle()

        // update subscription-related data
        this.profile = event.detail
        this.updateSubscriberDetails()
        this.identityService.update(this.profile, false).catch(console.log)
    }

    private onIdentityChange(event: IIdentityEvent) {
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        this.identityService.update(this.profile, false).catch(console.log)

        // update ad service
        this.adService.update(this.profile.adLight).catch(console.log)

        // user signed in, has subscriptions and analytics not yet init'd, safe to init
        if (this.analyticsConf && !this.analyticsConf.profile && this.profile.email && this.profile.subscriptions) {
            this.analyticsConf = {
                ...this.analyticsConf,
                profile: this.profile
            }
            this.analyticsService.initialize(this.analyticsConf).catch(console.log)

        // update analytics service
        } else if (this.analyticsConf && this.analyticsConf.profile) {
            this.analyticsService.update(this.profile)
        }

        // update widget service
        this.widgetService.update(this.profile.adLight).catch(console.log)
    }

    /**
     * Actions requiring user profile.
     */
    private onIdentityInit(event: IIdentityEvent) {
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        this.identityService.update(this.profile, false).catch(console.log)

        // initialize age gate service profile status
        this.ageGateService.initialize(this.profile.ageOfMajority)

        this.pageAgeGateService.initialize(this.conf.page.ageGate, this.profile.ageOfMajority)

        // initialize ad service
        if (this.conf.ads && this.adService) {
            this.adService.initialize({
                adLight: this.profile.adLight,
                analyticsConfig: this.conf.analytics,
                ancestry: this.conf.page.ancestry,
                articleId: this.conf.page.articleId,
                bidding: this.conf.ads.bidding,
                negative: this.conf.page.klangoo ? this.conf.page.klangoo.negativeKeywords : undefined,
                network: this.conf.ads.network,
                originId: this.conf.page.originId,
                page: this.conf.page.type,
                pageConfig: this.conf.page,
                property: this.conf.ads.property,
                provider: this.conf.ads.provider,
                refresh: this.conf.ads.refresh,
                sensitive: this.conf.page.klangoo ? this.conf.page.klangoo.sensitiveContent : undefined,
                siteId: this.conf.ads.site,
                tagManagers: this.conf.ads.tags,
                verifier: this.conf.ads.verifier,
                writers: this.conf.page.writers
            })
                .catch(console.log)
        }

        // signed in and have subscriptions, safe to init analytics
        if (this.analyticsConf && this.profile.email && this.profile.subscriptions) {
            this.analyticsConf = {
                ...this.analyticsConf,
                profile: this.profile
            }
            this.analyticsService.initialize(this.analyticsConf).catch(console.log)
        } else {
            this.onIdentitySession(Utils.event(IdentityEvent.SESSION_INIT, this.profile))
        }

        // initialize widget service
        this.widgetService.initialize(this.profile.adLight).catch(console.log)
    }

    /**
     * Identity session init handler.
     */
    private onIdentitySession(event: IIdentityEvent) {
        this.profile = event.detail

        // user anon and analytics not yet init'd, safe to init
        if (this.analyticsConf && !this.analyticsConf.profile && !this.profile.email) {
            this.analyticsConf = {
                ...this.analyticsConf,
                profile: this.profile
            }
            this.analyticsService.initialize(this.analyticsConf).catch(console.log)
        }
    }

    private updateSubscriberDetails() {
        this.profile.adLight = (this.profile.subscriptions || []).reduce((pass, sub: ISubscription) => {
            return pass || (this.conf.identity.subscriber.resIds || []).indexOf(sub.id) >= 0
        }, false)
    }
}
